import { cn } from '@/lib/utils';
import { MUTED_PILL, PILL_BASE, tonePillClasses } from './evidencePrimitives';

/**
 * ⚠️ The no-tone default is NEUTRAL, not `warning`.
 *
 * It defaulted to amber until review, which painted every reason tag as a caution — including
 * `PicklistClosed`'s automatic door, whose reason is literally "all items fully posted". The `note` shape
 * carries no tone of its own precisely BECAUSE the server does not claim an outcome for a reason string;
 * inventing one on the client is the row asserting something the projection deliberately withheld. Colour
 * here comes only from the row's Severity, which the caller passes in.
 */


interface ReasonTagProps {
  text?: string | null;
  /**
   * The ROW's tone, passed down by the caller. The wire's `note` block carries no tone of its own because
   * severity lives on the activity row, not on the block — so the panel row hands its severity in here.
   */
  tone?: string | null;
  className?: string;
}

/**
 * `shape: 'note'` — the reason pill (design §1.4 "Reason tag").
 *
 * Defaults to `warning` because a note exists to explain a departure from the expected path — "Damaged in
 * transit", "Bin full", "Wrong quantity". Neutral would be the wrong default for a block that only appears
 * when something needed explaining; a caller with a `Critical` row overrides to `destructive`.
 *
 * **Nothing is truncated and nothing is clamped.** The reason is the whole point of the row, so the pill
 * wraps to a second line rather than hiding the half of the sentence that says why.
 */
export function ReasonTag({ text, tone, className }: ReasonTagProps) {
  if (!text || text.trim() === '') return null;

  const pill = tonePillClasses(tone) ?? MUTED_PILL;

  return (
    <span className={cn(PILL_BASE, 'max-w-full whitespace-normal break-words text-left', pill, className)}>
      {text}
    </span>
  );
}
